/**
 * Parent Option Item
 *
 * The destination flavour of {@link EntityResultRow}: the collection's path on
 * its own line, because in this modal the location IS what the user is choosing
 * between. Two collections named "Methods" under different parents rendered as
 * two identical rows here, and picking the wrong one was silent.
 *
 * The type badge every row used to carry is gone. It said "Collection" on every
 * row but the workspace, and it occupied exactly the space the path needed.
 *
 * The icon does NOT replace it for assistive tech — `BowerIcon` is
 * `aria-hidden`, and `role="option"` takes its accessible name from content —
 * so the word moves into a `VisuallyHidden` instead of disappearing. Sighted
 * users read the type off the icon; screen-reader users still hear it.
 */

import { EntityResultRow } from './EntityResultRow';
import { getEntityDisplayName } from '../../../theme/entityIcons';
import type { EntityType as ThemeEntityType } from '../../../theme/entityIcons';
import type { ParentOption } from './parentOptions.types';

interface ParentOptionItemProps {
  option: ParentOption;
  onSelect: () => void;
}

export function ParentOptionItem({ option, onSelect }: ParentOptionItemProps) {
  return (
    <EntityResultRow
      value={`${option.type}-${option.id}`}
      onSelect={onSelect}
      type={option.type}
      title={option.name}
      location={option.location}
      placement="block"
      typeLabel={getEntityDisplayName(option.type as ThemeEntityType)}
      // The workspace has nothing above it, so the computed breadcrumb would be
      // its own title echoed back on the second line.
      locationLabel={option.type === 'workspace' ? 'Workspace root' : undefined}
    />
  );
}
